// ENG-96 — per-surface sticky-pill state. See lib/email/stickyPill.ts for the rule.
//
// Lifecycle: sticky ids are scoped to an epoch = (pill, list-scope). On any epoch change the
// set is emptied DURING THE RENDER that observes the change. Within an epoch ids are only
// ever added — except an explicit manual Refresh, which empties the set.
//
// Deliberately NOT cleared by: search keystrokes (would re-introduce the jump mid-typing),
// collapse/deselect (the original bug on a delay), or the 60s poll / background refetch
// (clearing on a timer makes the row vanish at an arbitrary moment — the same bug, now
// non-deterministic).

import { useCallback, useRef, useState } from "react";
import { addSticky, EMPTY_STICKY, type StickyIds } from "@/lib/email/stickyPill";

export interface StickyPill {
  /** Read during render. Ref-backed, so pair it with `token` in any useMemo dep array. */
  ids: StickyIds;
  /** `${epochKey}#${version}` — the sound memo dependency (a ref-backed Set is not one). */
  token: string;
  /** Register a row this surface just patched. No-ops (and does not re-render) if known. */
  mark: (id: string) => void;
  /** Explicit "re-evaluate everything" — wire to the manual Refresh button only. */
  reset: () => void;
}

export function useStickyPill(epochKey: string): StickyPill {
  const idsRef = useRef<StickyIds>(EMPTY_STICKY);
  const epochRef = useRef(epochKey);
  const [version, setVersion] = useState(0);

  // Render-phase epoch reset (React's "adjust state when a prop changes"), NOT an effect —
  // an effect-based reset would render one frame of stale sticky rows under the new pill.
  // PRECONDITION: this mutates a ref during render, which is not rollback-safe if React
  // discards a render. Sound today because this app uses no concurrent features (no
  // StrictMode at the mount in main.tsx, no startTransition, no useDeferredValue, no
  // Suspense). If any of those arrive, move this to the useState form.
  if (epochRef.current !== epochKey) {
    epochRef.current = epochKey;
    idsRef.current = EMPTY_STICKY;
  }

  const mark = useCallback((id: string) => {
    const next = addSticky(idsRef.current, id);
    // Identical reference === the id was already sticky. Bailing here is load-bearing:
    // EmailRow's ?email= deep-link path calls markReadLocal from an effect, and an
    // unconditional setState there is an infinite-render-loop waiting to happen.
    if (next === idsRef.current) return;
    idsRef.current = next;
    setVersion((v) => v + 1);
  }, []);

  const reset = useCallback(() => {
    if (idsRef.current === EMPTY_STICKY) return;
    idsRef.current = EMPTY_STICKY;
    setVersion((v) => v + 1);
  }, []);

  return { ids: idsRef.current, token: `${epochKey}#${version}`, mark, reset };
}
